import React, {useState, useCallback, useEffect} from "react";
import CustomDateRangeModal from "./CustomDateRangeModal";
import "./date-range-selector.scss";

export interface IDateRange {
    fromDate: Date;
    toDate: Date;
    label: string;
}

// Helper functions for Unix timestamp conversion
export const dateToStartOfDayTimestamp = (date: Date): number => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay.getTime();
};

export const dateToEndOfDayTimestamp = (date: Date): number => {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
};

export interface IDateRangeSelectorProps {
    selectedRange: IDateRange;
    onRangeChange: (range: IDateRange, period?: string) => void;
    initialPeriod?: DateRangePeriod;
    className?: string;
}

export type DateRangePeriod = "W" | "M" | "Q" | "S" | "Y" | "All" | "Custom";

const DateRangeSelector: React.FC<IDateRangeSelectorProps> = ({
    selectedRange,
    onRangeChange,
    initialPeriod = "S",
    className = "",
}) => {
    const [activePeriod, setActivePeriod] = useState<DateRangePeriod>(initialPeriod);
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [hasCustomSelection, setHasCustomSelection] = useState(false);
    const [previousPeriod, setPreviousPeriod] = useState<DateRangePeriod>(initialPeriod);

    // Update active period when initialPeriod prop changes
    useEffect(() => {
        setActivePeriod(initialPeriod);
        setPreviousPeriod(initialPeriod);

        // Check if we have a custom date range from saved preferences
        if (initialPeriod === "Custom" || selectedRange.label.includes(" - ")) {
            setHasCustomSelection(true);
        }
    }, [initialPeriod, selectedRange]);

    // Calculate date ranges
    const getDateRange = useCallback(
        (period: DateRangePeriod): IDateRange => {
            const today = new Date();
            const fromDate = new Date();

            switch (period) {
                case "W": // Week
                    fromDate.setDate(today.getDate() - 7);
                    return {
                        fromDate,
                        toDate: today,
                        label: "Last 7 days",
                    };
                case "M": // Month
                    fromDate.setMonth(today.getMonth() - 1);
                    return {
                        fromDate,
                        toDate: today,
                        label: "Last 30 days",
                    };
                case "Q": // Quarter
                    fromDate.setMonth(today.getMonth() - 3);
                    return {
                        fromDate,
                        toDate: today,
                        label: "Last 3 months",
                    };
                case "S": // 6 months
                    fromDate.setMonth(today.getMonth() - 6);
                    return {
                        fromDate,
                        toDate: today,
                        label: "Last 6 months",
                    };
                case "Y": // Year
                    fromDate.setFullYear(today.getFullYear() - 1);
                    return {
                        fromDate,
                        toDate: today,
                        label: "Last 12 months",
                    };
                case "All": // All time (last 5 years)
                    fromDate.setFullYear(today.getFullYear() - 5);
                    return {
                        fromDate,
                        toDate: today,
                        label: "All time",
                    };
                default:
                    return selectedRange;
            }
        },
        [selectedRange]
    );

    const handlePeriodClick = useCallback(
        (period: DateRangePeriod) => {
            if (period === "Custom") {
                // Save the current period as previous before switching to Custom
                setPreviousPeriod(activePeriod);
                setActivePeriod("Custom");
                setShowCustomModal(true);
                return;
            }

            // Immediately set the active period for visual feedback
            setActivePeriod(period);
            setPreviousPeriod(period); // Update previous period when selecting a new period
            const range = getDateRange(period);
            onRangeChange(range, period);
        },
        [getDateRange, onRangeChange, activePeriod]
    );

    const handleCustomDateApply = useCallback(
        (startDate: Date, endDate: Date) => {
            const range: IDateRange = {
                fromDate: startDate,
                toDate: endDate,
                label: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
            };

            setActivePeriod("Custom");
            setHasCustomSelection(true);
            onRangeChange(range, "Custom");
        },
        [onRangeChange]
    );

    const handleCustomDateCancel = useCallback(() => {
        // Revert back to the previous period
        setActivePeriod(previousPeriod);
        const range = getDateRange(previousPeriod);
        onRangeChange(range, previousPeriod);
        setShowCustomModal(false);
    }, [previousPeriod, getDateRange, onRangeChange]);

    const handleModalClose = useCallback(() => {
        setShowCustomModal(false);
    }, []);

    return (
        <div className={`date-range-selector ${className}`}>
            <div className="date-range-selector__display">
                <span className="date-range-selector__label">
                    <b>From</b> {selectedRange.fromDate.toLocaleDateString()} <b>To</b>{" "}
                    {selectedRange.toDate.toLocaleDateString()}
                </span>
            </div>

            <div className="date-range-selector__buttons">
                {(["W", "M", "Q", "S", "Y", "All"] as DateRangePeriod[]).map((period) => (
                    <button
                        key={period}
                        type="button"
                        className={`date-range-selector__button ${
                            activePeriod === period ? "date-range-selector__button--active" : ""
                        }`}
                        onClick={() => handlePeriodClick(period)}
                    >
                        {period}
                    </button>
                ))}
                <button
                    type="button"
                    className={`date-range-selector__button date-range-selector__button--custom ${
                        activePeriod === "Custom" ? "date-range-selector__button--active" : ""
                    }`}
                    onClick={() => handlePeriodClick("Custom")}
                >
                    <i className="g-icon g-icon-calendar"></i>
                </button>
            </div>

            {/* Custom Date Range Modal */}
            <CustomDateRangeModal
                isOpen={showCustomModal}
                initialStartDate={hasCustomSelection ? selectedRange.fromDate : undefined}
                initialEndDate={hasCustomSelection ? selectedRange.toDate : undefined}
                onClose={handleModalClose}
                onApply={handleCustomDateApply}
                onCancel={handleCustomDateCancel}
            />
        </div>
    );
};

export default DateRangeSelector;
